import { apiClient, type ApiResponse } from './client'

export type TrialSetCode = 'A' | 'B' | 'C' | 'D' | 'E'
export type TrialStatus = 'PENDING' | 'ACTIVE' | 'EXPIRED' | 'CONVERTED' | 'REVOKED'

export interface TrialCatalogItem {
  trialSetCode: TrialSetCode
  productId: number
  productName: string
  curriculumId: number
  lessonCount: number
}

export interface TrialEligibility {
  eligible: boolean
  reason: string | null
}

export interface TrialView {
  id: number
  organizationId: number
  userId: number
  learnerName: string | null
  timeslotId: number
  timeslotName: string | null
  trialSetCode: TrialSetCode
  productId: number
  productName: string | null
  learningEnrollmentId: number | null
  status: TrialStatus
  periodStart: string | null
  periodEnd: string | null
  remainingDays: number | null
  grantedBy: number
  grantedAt: string
  revokedReason: string | null
  /** 회차 소진 여부 — 종결 회차는 스냅샷, current 회차는 실시간 판정 */
  consumed: boolean
  consumedAt: string | null
  /** revoke 응답에서만 채워짐 — 종결 직후 파생 상태(정상 케이스 WAITING) */
  learnerStatus?: string | null
}

export interface TrialProgramSummary {
  enabled: boolean
  firstGrantedOn: string | null
  closesOn: string | null
  /** @deprecated grantedAttemptCount와 동일 값 (하위 호환) */
  grantedCount: number
  grantedAttemptCount: number
  consumedAttemptCount: number
  unusedRevokedAttemptCount: number
  remainingGrants: number
  open: boolean
}

export interface TrialEffectAnalysis {
  grantedCount: number
  grantedAttemptCount: number
  consumedAttemptCount: number
  unusedRevokedAttemptCount: number
  startedCount: number
  pendingCount: number
  activeCount: number
  expiredCount: number
  convertedCount: number
  revokedCount: number
  conversionRatePercent: number
  byTrialSet: Record<TrialSetCode, number>
  lessonStartedCount: number
  lessonCompletedCount: number
  componentStartedCount: number
  componentCompletedCount: number
  averageCompletedLessonsInSevenDays: number
  averageHoursToPayment: number | null
  byGrantedDate: Record<string, number>
  trialSetToFormalProduct: Record<string, number>
}

export interface TrialSetChangeResponse {
  trial: TrialView
  previousLcEnrollmentId: number
  currentLcEnrollmentId: number
  auditId: number
}

export const freeTrialApi = {
  catalog: () => apiClient.get<ApiResponse<TrialCatalogItem[]>>('/lc/admin/free-trials/catalog'),
  eligibility: (userId: number) =>
    apiClient.get<ApiResponse<TrialEligibility>>(`/lc/admin/free-trials/eligibility/${userId}`),
  /** 회차 이력(최신순) — 과거 종결 회차 포함 */
  history: (userId: number) =>
    apiClient.get<ApiResponse<TrialView[]>>(`/lc/admin/free-trials/users/${userId}`),
  list: (timeslotId?: number) =>
    apiClient.get<ApiResponse<TrialView[]>>('/lc/admin/free-trials', { params: { timeslotId } }),
  grant: (body: { userId: number; timeslotId: number; trialSetCode: TrialSetCode }) =>
    apiClient.post<ApiResponse<TrialView>>('/lc/admin/free-trials', body),
  revoke: (id: number, reason: string) =>
    apiClient.post<ApiResponse<TrialView>>(`/lc/admin/free-trials/${id}/revoke`, { reason }),
  changeSet: (id: number, body: { trialSetCode: TrialSetCode; reason: string }) =>
    apiClient.post<ApiResponse<TrialSetChangeResponse>>(`/lc/admin/free-trials/${id}/change-set`, body),
  program: () => apiClient.get<ApiResponse<TrialProgramSummary>>('/lc/admin/free-trials/program'),
  analytics: () => apiClient.get<ApiResponse<TrialEffectAnalysis>>('/lc/admin/free-trials/analytics'),
}
